import { JobDetailType } from "@devforce/trailhead-client-gql";
import { I18n } from "@devforce/trailhead-client-locale";
import { CommunityNodeSummaryType } from "common/contentCollection";
import type { StampFromServer, StampSummary } from "common/stamp";

import { getAssetsUrl } from "common/helpers";
import type { LearningKindCollectionType, LearningType } from "common/learning";

import type { ContentSummary } from "tds/contentSummary";

import topicPlaceholder from "./assets/topic-placeholder.svg";
import * as locales from "./locales";

export type { ContentSummary };

const i18n = new I18n({ contentSummary: locales });

export function fromLearning(learning: LearningType): ContentSummary {
  switch (learning.type as LearningKindCollectionType) {
    case "MODULE":
      return fromModule(learning);
    case "PROJECT":
      return fromProject(learning);
    case "SUPERBADGE":
      return fromSuperbadge(learning);
    case "TRAIL":
      return fromTrail(learning);
    case "TRAILMIX":
      return fromTrailmix(learning);
    default:
      throw new Error(`Unsupported type: "${learning.type}"`);
  }
}

export function fromCommunity(
  community: CommunityNodeSummaryType
): ContentSummary {
  const contentType = community.type.toLowerCase() as ContentSummary["type"];
  let avatar = community.avatar;
  if (contentType === "topic" && !avatar) {
    avatar = getAssetsUrl(topicPlaceholder);
  }
  return {
    id: community.id,
    type: contentType,
    title: community.name,
    pretitle: community.typeLocalized,
    image: avatar,
    path: community.url,
    descriptionHtml: community.description,
  };
}

function getJobSalary(minSalary: string | null, maxSalary: string | null) {
  if (minSalary && maxSalary) {
    return `${minSalary}-${maxSalary}`;
  }
  return maxSalary ?? minSalary ?? "";
}

export function fromJob(job: JobDetailType): ContentSummary {
  return {
    id: job.id,
    descriptionHtml: job.summary,
    path: job.url,
    pathTarget: "_blank",
    pretitle: job.company,
    title: job.title,
    type: "job",
    dynamicCtaIcon: true,
    dynamicCtaIconSymbol: "link",
    details: [
      { icon: "location", label: job.location ?? "" },
      { icon: "money", label: getJobSalary(job.minSalary, job.maxSalary) },
      {
        icon: "clock",
        label: job.postedDate
          ? i18n.t("contentSummary", "job_detail_posted_on", {
              date: job.postedDate,
            })
          : "",
      },
    ].filter((detail) => !!detail.label),
  };
}

export function fromLearningBase(learning: LearningType): ContentSummary {
  return {
    color: learning.color ?? undefined,
    description: learning.description,
    id: learning.id,
    image: learning.iconUrl ?? "",
    estimatedTime: learning.minuteTotal ?? undefined,
    path: learning.url ?? undefined,
    points: learning.pointTotal ?? undefined,
    pretitle: i18n.t(
      "contentSummary",
      `${learning.type.toLowerCase()}_pretitle`
    ),
    title: learning.label,
    type: learning.type.toLowerCase() as ContentSummary["type"],
    user: learning.progress
      ? {
          finishedAt: learning.progress.completedDate ?? undefined,
          pointsEarned: learning.progress.earnedPoints ?? undefined,
          percentageComplete: learning.progress.completedPercent ?? undefined,
        }
      : undefined,
  };
}

export function fromModule(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromProject(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromStamp(stamp: StampFromServer): StampSummary {
  return {
    id: stamp.rewardId,
    name: stamp.name,
    description: stamp.description,
    imageURL: stamp.iconUrl,
    variant: stamp.kind,
    eventDate: stamp.eventDate,
    eventLocation: stamp.eventLocation,
    type: "stamp",
  };
}

export function fromSuperbadge(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromTrail(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
  };
}

export function fromTrailmix(learning: LearningType): ContentSummary {
  return {
    ...fromLearningBase(learning),
    pretitle: i18n.t("contentSummary", "trailmix_pretitle", {
      name: learning.creatorName,
    }),
  };
}

export function toSentenceCase(s: string) {
  return s.slice(0, 1).toUpperCase() + s.slice(1).toLowerCase();
}
